import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import NumberField from "../../../Components/NumberField";
import Handler from "../../../Helpers/Handler";
import { firstError, fuelRowsErrors, invalidClass, odometerError, RULES } from "../../../Helpers/bookingRules";
import ProfileAvatar from "../../../Assets/images/avatar.png";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import tripUrls from "./tripUrls";

// Swap car (Booking Flow v2, D9): the customer already has the car and it has to be replaced during the
// trip. Two sets of readings go in one request: the OLD car's end photos, odometer and fuel (they close
// its leg), and the NEW car's start photos, odometer and fuel (they open the next one). At Complete trip
// the km, fuel and FASTag of every leg are added up. The booking's dates and price do not change.
// Not offered once the trip has been extended or while an extension is waiting for payment.
// The admin and staff panels use this same file; only tripUrls differs.

const PhotoPicker = ({ label, hint, min, max, photos, setPhotos, disabled }) => {
    const add = (e) => {
        const files = Object.values(e.target.files || {});
        if (!files.length) return;
        let room = max - photos.length;
        if (files.length > room) toast.error(`Only ${max} photos are allowed; ${Math.max(room, 0)} more can be added`);
        files.forEach((file) => {
            if (room <= 0) return;
            if (!/^image\//.test(file.type)) { toast.error(`${file.name} is not an image`); return; }
            if (file.size > RULES.IMAGE_MAX_BYTES) { toast.error("Image must be below 10 MB"); return; }
            room -= 1;
            setPhotos((prev) => [...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, url: URL.createObjectURL(file) }]);
        });
        e.target.value = "";
    };
    return (
        <div className="mb-3">
            <div className="fw-semibold small mb-1">{label}</div>
            <div className="form-text mb-2">{hint} ({min} to {max}; {photos.length} added)</div>
            <div className="d-flex flex-wrap gap-2">
                {photos.map((p) => (
                    <div key={p.id} className="position-relative">
                        <img src={p.url} alt="" width={72} height={72} className="rounded-3 border" style={{ objectFit: "cover" }} />
                        <button type="button" className="btn btn-sm btn-danger p-0 rounded-circle position-absolute" style={{ top: -6, right: -6, width: 20, height: 20, lineHeight: "20px" }} onClick={() => setPhotos((prev) => prev.filter((v) => v.id !== p.id))} disabled={disabled} title="Remove">
                            <ImCross size={8} />
                        </button>
                    </div>
                ))}
                {photos.length < max && (
                    <label className="d-flex align-items-center justify-content-center rounded-3 border text-muted" style={{ width: 72, height: 72, cursor: disabled ? "not-allowed" : "pointer" }}>
                        <FaCloudUploadAlt size={20} />
                        <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="d-none" onChange={add} disabled={disabled} />
                    </label>
                )}
            </div>
        </div>
    );
};

const FuelFields = ({ rows, setRows, errors, setErrors, disabled }) => (
    <div className="row g-2 mb-3">
        {rows.map((f, i) => (
            <div className="col-6" key={f.fuelType + i}>
                <label className="form-label small"><span className="text-capitalize">{f.fuelType}</span> % <span className="text-muted">(0 to 100)</span></label>
                <NumberField
                    min={0}
                    max={100}
                    className={`form-control${invalidClass(errors?.[i])}`}
                    value={f.percentage}
                    onChange={(v) => { setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, percentage: v } : r))); setErrors((prev) => ({ ...(prev || {}), [i]: null })); }}
                    disabled={disabled}
                    aria-invalid={!!errors?.[i]}
                />
                {errors?.[i] && <div className="invalid-feedback">{errors[i]}</div>}
            </div>
        ))}
    </div>
);

const fuelRowsFor = (types) => (types?.length ? types : ["Fuel"]).map((fuelType) => ({ fuelType, percentage: "" }));

const SwapCarModal = ({ bookingId, currentCarId, endDate, perHourKm, fuelTypes, startKm, endLimits, startLimits, onClose, onSwapped }) => {
    const endMin = Number(endLimits?.min) || RULES.TRIP_IMAGES_MIN;
    const endMax = Number(endLimits?.max) || RULES.TRIP_IMAGES_MAX;
    const startMin = Number(startLimits?.min) || RULES.TRIP_IMAGES_MIN;
    const startMax = Number(startLimits?.max) || RULES.TRIP_IMAGES_MAX;

    const [loading, setLoading] = useState(true);
    const [cars, setCars] = useState([]);
    const [carId, setCarId] = useState(null);
    const [oldKm, setOldKm] = useState("");
    const [oldFuel, setOldFuel] = useState(() => fuelRowsFor(fuelTypes));
    const [oldPhotos, setOldPhotos] = useState([]);
    const [newKm, setNewKm] = useState("");
    const [newFuel, setNewFuel] = useState([]);
    const [newPhotos, setNewPhotos] = useState([]);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const chosen = cars.find((c) => c.carId === carId);

    useEffect(() => {
        (async () => {
            const now = new Date();
            const end = new Date(endDate);
            const response = await Handler({
                method: "post",
                url: tripUrls.availableCars,
                data: { startDate: now.toISOString(), endDate: (end > now ? end : now).toISOString(), carChangeFor: bookingId, carChangeSwap: true },
            });
            if (response?.success) {
                const list = response?.data?.data?.list || [];
                setCars(list.filter((c) => c.carId !== currentCarId && (perHourKm == null || (c.priceTypes || []).some((p) => `${p?.perHourKm}` === `${perHourKm}`))));
            } else {
                toast.error(response?.data?.message || "Could not load available cars");
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pick = (car) => {
        setCarId(car.carId);
        setNewFuel(fuelRowsFor(car.fuelType));
        setErrors((prev) => ({ ...prev, car: null, newFuel: null }));
    };

    const submit = async () => {
        const found = {
            car: carId ? null : "Pick the replacement car",
            oldKm: odometerError(oldKm, { label: "Old car's odometer", floor: startKm, floorLabel: "its start reading" }),
            oldFuel: fuelRowsErrors(oldFuel),
            oldPhotos: oldPhotos.length < endMin ? `Add at least ${endMin} photos of the old car (${oldPhotos.length} so far)` : null,
            newKm: odometerError(newKm, { label: "New car's odometer" }),
            newFuel: fuelRowsErrors(newFuel),
            newPhotos: newPhotos.length < startMin ? `Add at least ${startMin} photos of the new car (${newPhotos.length} so far)` : null,
        };
        setErrors(found);
        const problem = firstError(found);
        if (problem) { toast.error(problem); return; }

        const form = new FormData();
        form.append("carId", carId);
        form.append("endKm", String(Number(oldKm)));
        form.append("endFuel", JSON.stringify(oldFuel.map((f) => ({ fuelType: f.fuelType, percentage: Number(f.percentage) }))));
        form.append("startKm", String(Number(newKm)));
        form.append("startFuel", JSON.stringify(newFuel.map((f) => ({ fuelType: f.fuelType, percentage: Number(f.percentage) }))));
        oldPhotos.forEach((p) => form.append("oldEndImages", p.file));
        newPhotos.forEach((p) => form.append("newStartImages", p.file));

        setBusy(true);
        const response = await Handler({ method: "patch", url: tripUrls.swap(bookingId), data: form, formdata: true });
        setBusy(false);
        if (response?.success) {
            toast.success("Car swapped. The customer has been told");
            onSwapped?.();
            onClose?.();
        } else {
            toast.error(response?.data?.message || "Could not swap the car");
        }
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Swap Car During Trip</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={busy}></button>
                        </div>
                        <div className="modal-body">
                            <div className="small text-muted mb-3">
                                The customer keeps the trip and its price. Record the old car as it is handed back, then the new car as it is handed over. At Complete trip, km, fuel and FASTag of both cars are added up.
                            </div>

                            <div className="fw-semibold mb-1">1. New car</div>
                            <div className="form-text mb-2">Cars free for the rest of this trip with the same package.</div>
                            {loading ? (
                                <div className="text-muted small mb-3">Loading available cars...</div>
                            ) : cars.length === 0 ? (
                                <div className="text-muted small mb-3">No matching cars are free for the rest of this trip.</div>
                            ) : (
                                <div className={`d-flex flex-column gap-2 mb-3${errors.car ? " border border-danger rounded-3 p-1" : ""}`} style={{ maxHeight: 240, overflowY: "auto" }}>
                                    {cars.map((c) => (
                                        <div key={c.carId} className={`d-flex align-items-center gap-3 p-2 rounded-3 border ${carId === c.carId ? "border-danger bg-danger bg-opacity-10" : ""}`} style={{ cursor: "pointer" }} onClick={() => !busy && pick(c)}>
                                            <input type="radio" checked={carId === c.carId} onChange={() => pick(c)} disabled={busy} />
                                            <img src={c.carImages?.[0] || ProfileAvatar} alt={c.registrationNo} width={48} height={48} className="rounded-3 flex-shrink-0" style={{ objectFit: "cover" }} />
                                            <div className="flex-grow-1">
                                                <div className="fw-semibold small">{[c.brand, c.modelName].filter(Boolean).join(" ") || c.registrationNo}</div>
                                                <div className="text-muted small">{c.registrationNo} · {c.hub || "-"}</div>
                                            </div>
                                            <div className="text-muted small text-capitalize">{[c.segment, c.subSegment].filter(Boolean).join(" · ")}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="fw-semibold mb-2">2. Old car, as handed back</div>
                            <div className="mb-3">
                                <label className="form-label small">Odometer (km)</label>
                                <NumberField min={startKm ?? 0} max={RULES.ODOMETER_MAX} className={`form-control${invalidClass(errors.oldKm)}`} value={oldKm} onChange={(v) => { setOldKm(v); setErrors((p) => ({ ...p, oldKm: null })); }} disabled={busy} aria-invalid={!!errors.oldKm} />
                                {errors.oldKm && <div className="invalid-feedback">{errors.oldKm}</div>}
                                {startKm !== undefined && startKm !== null && <div className="form-text">It started this trip at {startKm} km.</div>}
                            </div>
                            <div className="fw-semibold small mb-1">Fuel level</div>
                            <FuelFields rows={oldFuel} setRows={setOldFuel} errors={errors.oldFuel} setErrors={(fn) => setErrors((p) => ({ ...p, oldFuel: typeof fn === "function" ? fn(p.oldFuel) : fn }))} disabled={busy} />
                            <PhotoPicker label="Photos of the old car" hint="All sides, as you would at hand-back" min={endMin} max={endMax} photos={oldPhotos} setPhotos={setOldPhotos} disabled={busy} />
                            {errors.oldPhotos && <div className="text-danger small mb-3">{errors.oldPhotos}</div>}

                            <div className="fw-semibold mb-2 mt-4">3. New car, as handed over</div>
                            {!chosen ? (
                                <div className="text-muted small mb-3">Pick the new car above first.</div>
                            ) : (
                                <>
                                    <div className="mb-3">
                                        <label className="form-label small">Odometer (km)</label>
                                        <NumberField min={0} max={RULES.ODOMETER_MAX} className={`form-control${invalidClass(errors.newKm)}`} value={newKm} onChange={(v) => { setNewKm(v); setErrors((p) => ({ ...p, newKm: null })); }} disabled={busy} aria-invalid={!!errors.newKm} />
                                        {errors.newKm && <div className="invalid-feedback">{errors.newKm}</div>}
                                    </div>
                                    <div className="fw-semibold small mb-1">Fuel level</div>
                                    <FuelFields rows={newFuel} setRows={setNewFuel} errors={errors.newFuel} setErrors={(fn) => setErrors((p) => ({ ...p, newFuel: typeof fn === "function" ? fn(p.newFuel) : fn }))} disabled={busy} />
                                    <PhotoPicker label="Photos of the new car" hint="All sides, before the customer drives away" min={startMin} max={startMax} photos={newPhotos} setPhotos={setNewPhotos} disabled={busy} />
                                    {errors.newPhotos && <div className="text-danger small mb-3">{errors.newPhotos}</div>}
                                </>
                            )}
                            <div className="form-text">FASTag balances of both cars are read automatically.</div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
                            <button type="button" className="btn btn-primary" onClick={submit} disabled={busy || !carId}>{busy ? "Swapping..." : "Swap Car"}</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default SwapCarModal;
